import React, { useState, useMemo, useEffect } from "react";
import { PAGINATION_PAGE_SIZE_OPTIONS } from "../../config/pagination";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { formatSalary } from "../../components/common/formatSalary";
import { DATE_DISPLAY_FORMAT, displayDate, toApiDate } from "../../utils/dateUtils";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Grid,
  Chip,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  
  Stepper,
  Step,
  StepLabel,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  LinearProgress,
  Tooltip,
  FormControlLabel,
  Checkbox,
  Snackbar,
  Switch,
  CircularProgress,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import StatusDropdown from "../../components/common/StatusDropdown";
import {
  Add,
  Edit,
  Delete,
  Search,
  Visibility,
  Close,
  Download,
  Upload,
  Email,
  Phone,
  CloudUpload,
  CheckCircle,
  Error,
  Description,
  FileDownload,
  AccessTime,
} from "@mui/icons-material";
//import PhoneInput from "react-phone-number-input";
//import "react-phone-number-input/style.css";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/material.css";
import AddressAutocomplete from "../../components/common/AddressAutoComplete";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../../services/api";
import rbacService from "../../services/rbacService";
import DataImportWizard from "../../components/common/DataImportWizard";
import PermissionGate from "../../components/common/PermissionGate";
import DepartmentModuleFilterSelect from "../../components/common/DepartmentModuleFilterSelect";
import { useDepartmentModuleFilter } from "../../hooks/useDepartmentModuleFilter";
import { usePermissions } from "../../hooks/usePermissions";
import { revealTeacherPii } from "../../services/piiService";
import { PII_PERMISSIONS } from "../../utils/piiPermissions";

const Teachers = ({
  openFromParent = false,
  parentRole = "teacher",
  openEditFromParent = false,
  parentEditData = null,
  parentPrefillLoading = false,
  onParentClose,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedInstitute } = useSelector((state) => state.instituteContext || {});
  const authUser = useSelector((state) => state.auth?.user);
  const {
    departmentFilter,
    setDepartmentFilter,
    showAllDepartmentsOption,
    departmentApiParams,
    effectiveInstituteId,
  } = useDepartmentModuleFilter();

  // Get current user from localStorage (fallback for instituteId)
  const [currentUser, setCurrentUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingTeacherId, setEditingTeacherId] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [teacherToDelete, setTeacherToDelete] = useState(null);
  const [subjectsDialogOpen, setSubjectsDialogOpen] = useState(false);
  const [subjectsDialogList, setSubjectsDialogList] = useState([]);
  const formatDateForInput = (date) => {
  if (!date) return "";
  return new Date(date).toISOString().split("T")[0];
};


  const getEmptyFormData = () => ({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    gender: "",
    role: "teacher",
    qualification: "",
    experienceYears: "",
    designation: "",
    salary: "",
    departmentId: "",
    dob: "",
    joiningDate: "",
    street: "",
    city: "",
    state: "",
    country: "",
    pinCode: "",
  });

  // API query parameters
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
  });
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("firstName");
  const [order, setOrder] = useState("DESC");
  const [role, setRole] = useState("");
  const [formData, setFormData] = useState(getEmptyFormData());
  const [phoneError, setPhoneError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  // Import states
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  // Snackbar states
  const [departments, setDepartments] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [togglingTeacherId, setTogglingTeacherId] = useState(null);
  const [optimisticStatus, setOptimisticStatus] = useState({});

  // ---- PII reveal (unmask) ----
  const { hasPermission } = usePermissions();
  const canRevealTeacherPii = hasPermission(PII_PERMISSIONS.TEACHER_REVEAL);

  // A value is considered masked when the backend returns a "***"-style marker.
  const looksMasked = (v) => typeof v === "string" && v.includes("***");

  // PII fields exposed by this form/detail view.
  const TEACHER_PII_FIELDS = ["email", "phoneNumber", "dob", "salary"];

  // True when at least one PII field in the given object is still masked.
  const hasMaskedTeacherPii = (obj) => {
    if (!obj || typeof obj !== "object") return false;
    return TEACHER_PII_FIELDS.some((f) => looksMasked(obj?.[f]));
  };

  const [revealingTeacherPii, setRevealingTeacherPii] = useState(false);

  // Reveal real PII for a teacher and merge into LOCAL component state only.
  // Never persisted to redux/localStorage.
  const handleRevealTeacherPii = async (teacherId) => {
    if (!teacherId || revealingTeacherPii) return;
    setRevealingTeacherPii(true);
    try {
      const res = await revealTeacherPii(teacherId);
      const real = res?.data?.data || {};

      // Only merge recognised PII fields that the backend actually returned.
      const pickPii = (target) => {
        const next = { ...(target || {}) };
        if (real.email !== undefined && real.email !== null) next.email = real.email;
        // Phone is stored raw here (react-phone-input-2 uses the raw string).
        // The detail dialog renders `selectedTeacher.phone` first, so overwrite
        // BOTH `.phone` and `.phoneNumber` — otherwise the masked `.phone`
        // (from the grid row mapping) keeps winning and the reveal appears to do nothing.
        if (real.phoneNumber !== undefined && real.phoneNumber !== null) {
          next.phoneNumber = real.phoneNumber;
          next.phone = real.phoneNumber;
        }
        if (real.dob !== undefined && real.dob !== null) next.dob = real.dob;
        // salary may be a number (0 is a valid value) — merge when present.
        if (real.salary !== undefined && real.salary !== null) next.salary = real.salary;
        return next;
      };

      // Edit form state: revealed fields fill real values and stay editable.
      setFormData((prev) => (prev ? pickPii(prev) : prev));

      // Detail view state: raw real values.
      setSelectedTeacher((prev) => (prev ? pickPii(prev) : prev));
    } catch (e) {
      // Keep masked values on failure; never crash.
      setSnackbar({
        open: true,
        message:
          e?.response?.data?.message || "Could not reveal personal information",
        severity: "error",
      });
    } finally {
      setRevealingTeacherPii(false);
    }
  };



  const queryClient = useQueryClient();

  // Debounce search input to avoid too many API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchQuery.trim());
      // Reset to page 1 when search changes
      setPagination((prev) => ({ ...prev, page: 1 }));
      setPaginationModel((prev)=>({ ...prev, page: 0 }));
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 1 }));
    setPaginationModel((prev)=>({ ...prev, page: 0 }));
  }, [departmentFilter]);

  // ========= API: Institute Teachers Listing =========
  // Uses: GET /institute/get-all-teachers?search=&page=1&limit=20&sort=firstName&order=DESC&role=
  const { data: teachersResponse, isLoading: teachersLoading, refetch: refetchTeachers, isRefetching } = useQuery({
    queryKey: ["institute-teachers", {
      search,
      page: pagination.page,
      limit: pagination.limit,
      sort,
      order,
      role,
      instituteId: currentUser?.instituteId ?? selectedInstitute?.id,
      departmentId: departmentApiParams.departmentId ?? null,
    }],
    
    queryFn: async () => {
      const params = {
        search: search || "",
        page: pagination.page,
        limit: pagination.limit,
        sort: sort || "firstName",
        order: order || "DESC",
        role: role || "",
      };
      const instituteId = currentUser?.instituteId ?? selectedInstitute?.id;
      if (instituteId) params.instituteId = instituteId;
      if (departmentApiParams.departmentId) {
        params.departmentId = departmentApiParams.departmentId;
      }
      const res = await api.get("/institute/get-all-teachers", { params });
      return res.data;
    },
    staleTime: 0, // Always consider data stale, so it refetches when invalidated
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });

  const paginationInfo =
  teachersResponse?.data?.pagination ||
  teachersResponse?.pagination || {
    total: 0,
    page: pagination.page,
    totalPages: 1,
  };
  useEffect(()=>{setPaginationModel({page:Math.max(0,(Number(pagination.page)||1)-1),pageSize:Number(pagination.limit)||10});},[pagination.page,pagination.limit]);


  // Normalize API response into rows expected by DataGrid
  const teachers = useMemo(() => {
    // Try common shapes: { data: { teachers: [] } }, { data: { rows: [] } }, { data: [] }
    const raw =
      teachersResponse?.data?.teachers ||
      teachersResponse?.data?.rows ||
      teachersResponse?.data?.items ||
      teachersResponse?.data ||
      teachersResponse?.teachers ||
      teachersResponse?.rows ||
      teachersResponse?.items ||
      [];

    return (raw || []).map((t, index) => {
      const user = t.user || t.User || t.profile || t.userDetails || {};
      const teacher = t.teacher || t.Teacher || t.teacherDetails || t;

      const firstName =
        user.firstName || teacher.firstName || t.firstName || t.first_name || "";
      const lastName =
        user.lastName || teacher.lastName || t.lastName || t.last_name || "";
      const fullName =
        user.fullName ||
        teacher.fullName ||
        t.fullName ||
        user.name ||
        teacher.name ||
        t.name ||
        `${firstName} ${lastName}`.trim() ||
        `Teacher ${index + 1}`;

      const teacherId =
        teacher.id ||
        t.teacherId ||
        t.teacher_id ||
        t.teacherID ||
        null;

      const userId = user.id || t.userId || t.user_id || null;

      return {
        // DataGrid row id (must be stable/unique). Prefer teacher table id, else fallback.
        id: teacherId || userId || t.id || t.uuid || index + 1,
        teacherId: teacher.id || t.teacherId || t.id || null,
        userId: userId || t.userId || null,
        firstName,
        lastName,
        gender: teacher.gender || t.gender || user.gender || null,
        dob: teacher.dob || t.dob || null,
        role: teacher.role || t.role || "teacher",

        name: fullName,
        fullName: t.fullName || fullName,
        designation:
          teacher.designation || t.designation || t.role || t.position || "",
        email: user.email || t.email || t.emailId || "",
        phone:
          user.phoneNumber ||
          teacher.phoneNumber ||
          t.phoneNumber ||
          t.phone ||
          t.mobile ||
          "",
        phoneNumber: t.phoneNumber || teacher.phoneNumber || user.phoneNumber || "",
        departmentId: teacher.departmentId || t.departmentId || "",
        departmentName: t.departmentName || teacher.departmentName || "",
        qualification: teacher.qualification || t.qualification || "",
        experienceYears: teacher.experienceYears || t.experienceYears || "",
        salary: teacher.salary || t.salary || "",
        joiningDate: teacher.joiningDate || t.joiningDate || "",
        endingDate: teacher.endingDate || t.endingDate || null,
        teacherLoginID: t.teacherLoginID || teacher.teacherLoginID || "",
        street: teacher.street || t.street || "",
        city: teacher.city || t.city || "",
        state: teacher.state || t.state || "",
        country: teacher.country || t.country || "",
        pinCode: teacher.pinCode || t.pinCode || "",
        address: t.address || teacher.address || "",
        subjects: t.subjects || teacher.subjects || [],
        classes: t.classes || teacher.classes || [],
        status:
          (typeof t.status === "boolean"
            ? (t.status ? "active" : "inactive")
            : typeof t.status === "string"
              ? t.status.toLowerCase()
              : t.isActive === false
                ? "inactive"
                : t.isActive === true
                  ? "active"
                  : "active") || "active",
        isActive:
          typeof t.status === "boolean"
            ? t.status
            : typeof t.isActive === "boolean"
              ? t.isActive
              : t.status === "active" || t.status === "Active"
                ? true
                : false,
        joinDate:
          teacher.joinDate ||
          teacher.joiningDate ||
          t.joinDate ||
          t.joiningDate ||
          t.createdAt ||
          "",
        uuid: t.uuid || teacher.uuid || null,
        canDelete: t.canDelete !== undefined ? t.canDelete : true,
        createdAt: t.createdAt || teacher.createdAt || null,
        updatedAt: t.updatedAt || teacher.updatedAt || null,
      };
    });
  }, [teachersResponse]);

  // Fetch institute roles for the role dropdown
  const { data: rolesResponse } = useQuery({
    queryKey: ["institute-roles", selectedInstitute?.id],
    queryFn: () => {
      if (!selectedInstitute?.id) return { data: [] };
      return rbacService.getInstituteRoles(selectedInstitute.id);
    },
    enabled: !!selectedInstitute?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Prepare roles for dropdown - ensure teacher, admin, hod are always included
  const availableRoles = useMemo(() => {
    // Always include these core roles
    const coreRoles = [
      { slug: 'teacher', name: 'Teacher' },
      { slug: 'admin', name: 'Admin' },
      { slug: 'hod', name: 'HOD' }
    ];
    
    // Additional roles that were previously hardcoded
    const additionalRoles = [
      { slug: 'lecturer', name: 'Lecturer' },
      { slug: 'professor', name: 'Professor' },
      { slug: 'assistant professor', name: 'Assistant Professor' },
      { slug: 'associate professor', name: 'Associate Professor' },
      { slug: 'visiting faculty', name: 'Visiting Faculty' },
      { slug: 'adjunct faculty', name: 'Adjunct Faculty' },
      { slug: 'instructor', name: 'Instructor' },
      { slug: 'head of department', name: 'Head of Department' },
      { slug: 'department head', name: 'Department Head' },
      { slug: 'administrator', name: 'Administrator' }
    ];

    // Try to get API roles if available
    const apiRoles = rolesResponse?.data?.data || rolesResponse?.data || [];
    const apiRoleSlugs = apiRoles.map(role => role.slug?.toLowerCase()).filter(Boolean);
    
    // Add API roles that aren't already in core or additional roles
    const extraApiRoles = apiRoles
      .filter(role => role.slug && 
        !['teacher', 'admin', 'hod'].includes(role.slug.toLowerCase()) &&
        !additionalRoles.some(additional => additional.slug === role.slug.toLowerCase())
      )
      .map(role => ({
        slug: role.slug,
        name: role.name || role.slug.charAt(0).toUpperCase() + role.slug.slice(1)
      }));
    
    // Combine all roles
    return [...coreRoles, ...additionalRoles, ...extraApiRoles];
  }, [rolesResponse]);

  // ========= API: Create Teacher =========
  const createTeacherMutation = useMutation({
    mutationFn: async (payload) => {
      const res = await api.post("/institute/create-teacher", payload);
      return res.data;
    },
    onSuccess: async (data) => {

      // Close dialog first
      closeCreateDialog();

      // Reset form and clear errors
      setFormData(getEmptyFormData());
      setFieldErrors({});

      // Reset pagination to page 1 to see the new teacher
      setPagination({ page: 1, limit: 20 });

      // Show success message
      setSnackbar({
        open: true,
        message: data?.message || "Teacher created successfully!",
        severity: "success",
      });

      // Force remove from cache and refetch
      queryClient.removeQueries({
        queryKey: ["institute-teachers"],
        exact: false, // Remove all queries that start with this key
      });

      // Invalidate and immediately refetch
      await queryClient.invalidateQueries({
        queryKey: ["institute-teachers"],
        exact: false,
        refetchType: "all",
      });

      // Explicitly refetch to ensure update
      try {
        const result = await refetchTeachers();
      } catch (error) {
        console.error("Error refetching teachers:", error);
      }
    },
    onError: (error) => {
      // Show error message
      setSnackbar({
        open: true,
        message: error?.response?.data?.message || "Failed to create teacher. Please try again.",
        severity: "error",
      });
    },
  });

  // const handleCreateTeacher = () => {
  //   const payload = {
  //     firstName: formData.firstName?.trim(),
  //     lastName: formData.lastName?.trim(),
  //     email: formData.email?.trim(),
  //     password: "password123", // default password; backend can enforce change later
  //     phoneNumber: formData.phoneNumber,
  //     departmentId: formData.departmentId || 1,
  //     teacherLoginID:
  //       formData.teacherLoginID ||
  //       (formData.email ? formData.email.split("@")[0] : undefined),
  //   };

  //   createTeacherMutation.mutate(payload);
  // };

  const validateForm = () => {
    const errors = {};

    // Validate First Name - no spaces, no numbers, only letters, min 3 max 20 characters
    if (!formData.firstName || formData.firstName.trim() === "") {
      errors.firstName = "First Name is required";
    } else if (formData.firstName.trim().length < 3) {
      errors.firstName = "First Name must be at least 3 characters";
    } else if (formData.firstName.trim().length > 20) {
      errors.firstName = "First Name cannot exceed 20 characters";
    } else if (!/^[A-Za-z]+$/.test(formData.firstName.trim())) {
      errors.firstName = "First Name must contain only letters (no spaces or numbers)";
    }

    // Validate Last Name - no spaces, no numbers, only letters, min 3 max 20 characters (optional)
    if (formData.lastName && formData.lastName.trim() !== "") {
      if (formData.lastName.trim().length < 3) {
        errors.lastName = "Last Name must be at least 3 characters";
      } else if (formData.lastName.trim().length > 20) {
        errors.lastName = "Last Name cannot exceed 20 characters";
      } else if (!/^[A-Za-z]+$/.test(formData.lastName.trim())) {
        errors.lastName = "Last Name must contain only letters (no spaces or numbers)";
      }
    }

    // Validate Email
    if (!formData.email || formData.email.trim() === "") {
      errors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      errors.email = "Please enter a valid email address";
    }

    // Validate Phone Number (same as Profile.js: at least 10 digits, message "Invalid phone number")
    if (!formData.phoneNumber || formData.phoneNumber.trim() === "") {
      errors.phoneNumber = "Phone Number is required";
    } else {
      const digitsOnly = (formData.phoneNumber || "").replace(/\D/g, "");
      if (digitsOnly.length < 10) {
        errors.phoneNumber = "Invalid phone number";
      }
    }

    // Validate Date of Birth
    if (!formData.dob || formData.dob.trim() === "") {
      errors.dob = "Date of Birth is required";
    } else {
      const dobTrimmed = formData.dob.trim();
      const dobYearPart = dobTrimmed.split('-')[0] || '';
      if (dobYearPart.length > 4) {
        errors.dob = "Year cannot exceed 4 digits (YYYY). Please enter full date as YYYY-MM-DD.";
      } else if (/^\d{1,4}$/.test(dobTrimmed) || (dobYearPart.length > 0 && dobTrimmed.indexOf('-') === -1)) {
        errors.dob = "Please enter full date (YYYY-MM-DD), not only year.";
      } else {
      // Validate date format and range
      const dobRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (!dobRegex.test(dobTrimmed)) {
        errors.dob = "Please enter a valid date (YYYY-MM-DD)";
      } else {
        const dob = new Date(formData.dob);
        const currentYear = new Date().getFullYear();
        const birthYear = dob.getFullYear();
        
        // Check if date is valid
        if (isNaN(dob.getTime())) {
          errors.dob = "Please enter a valid date";
        } 
        // Check reasonable age range (16-100 years)
        else if (birthYear > currentYear - 16) {
          errors.dob = "Teacher must be at least 16 years old";
        } 
        else if (birthYear < currentYear - 100) {
          errors.dob = "Please enter a valid birth year";
        }
        // Check if date is not in future
        else if (dob > new Date()) {
          errors.dob = "Date of Birth cannot be in the future";
        }
      }
      }
    }

    // Validate Joining Date
    if (!formData.joiningDate || formData.joiningDate.trim() === "") {
      errors.joiningDate = "Joining Date is required";
    } else {
      const joiningTrimmed = formData.joiningDate.trim();
      const joiningYearPart = joiningTrimmed.split('-')[0] || '';
      if (joiningYearPart.length > 4) {
        errors.joiningDate = "Year cannot exceed 4 digits (YYYY). Please enter full date as YYYY-MM-DD.";
      } else if (/^\d{1,4}$/.test(joiningTrimmed) || (joiningYearPart.length > 0 && joiningTrimmed.indexOf('-') === -1)) {
        errors.joiningDate = "Please enter full date (YYYY-MM-DD), not only year.";
      } else {
      // Validate date format and range
      const joiningRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (!joiningRegex.test(joiningTrimmed)) {
        errors.joiningDate = "Please enter a valid date (YYYY-MM-DD)";
      } else {
        const joiningDate = new Date(formData.joiningDate);
        const currentYear = new Date().getFullYear();
        const joiningYear = joiningDate.getFullYear();
        
        // Check if date is valid
        if (isNaN(joiningDate.getTime())) {
          errors.joiningDate = "Please enter a valid date";
        }
        // Check reasonable joining year range (not too far in past or future)
        else if (joiningYear < currentYear - 50) {
          errors.joiningDate = "Joining year cannot be more than 50 years in the past";
        }
        else if (joiningYear > currentYear + 1) {
          errors.joiningDate = "Joining year cannot be more than 1 year in the future";
        }
        
        // Check if joining date is after DOB (if DOB is provided)
        if (formData.dob && !errors.dob) {
          const dob = new Date(formData.dob);
          if (!isNaN(dob.getTime()) && joiningDate <= dob) {
            errors.joiningDate = "Joining Date must be after Date of Birth";
          }
        }
      }
      }
    }

    // Validate Gender
    if (!formData.gender || formData.gender.trim() === "") {
      errors.gender = "Gender is required";
    }

    // Validate Qualification - min 2 max 100 characters
    if (!formData.qualification || formData.qualification.trim() === "") {
      errors.qualification = "Qualification is required";
    } else if (formData.qualification.trim().length < 2) {
      errors.qualification = "Qualification must be at least 2 characters";
    } else if (formData.qualification.trim().length > 40) {
      errors.qualification = "Qualification cannot exceed 40 characters";
    }

    // Validate Experience
    if (!formData.experienceYears || formData.experienceYears.toString().trim() === "") {
      errors.experienceYears = "Experience (Years) is required";
    } else {
      const expYears = formData.experienceYears.toString().trim();
      if (!/^\d{1,2}$/.test(expYears)) {
        errors.experienceYears = "Experience must be 1-2 digits";
      } else {
        const years = parseInt(expYears, 10);
        if (years < 0) {
          errors.experienceYears = "Experience cannot be negative";
        } else if (years > 30) {
          errors.experienceYears = "Experience cannot exceed 30 years";
        }
      }
    }

    // Validate Designation - min 2 max 50 characters
    if (!formData.designation || formData.designation.trim() === "") {
      errors.designation = "Designation is required";
    } else if (formData.designation.trim().length < 2) {
      errors.designation = "Designation must be at least 2 characters";
    } else if (formData.designation.trim().length > 30) {
      errors.designation = "Designation cannot exceed 30 characters";
    }

    // Validate Salary
    if (!formData.salary || formData.salary.toString().trim() === "") {
      errors.salary = "Salary is required";
    }

    // Validate Department
    if (!formData.departmentId || formData.departmentId === "") {
      errors.departmentId = "Department is required";
    }

    // Validate Pincode - no alphabets, only numbers
    if (formData.pinCode && formData.pinCode.trim() !== "") {
      if (!/^\d+$/.test(formData.pinCode.trim())) {
        errors.pinCode = "Pincode must contain only numbers (no alphabets)";
      } else if (formData.pinCode.trim().length < 3 || formData.pinCode.trim().length > 9) {
        errors.pinCode = "Pincode must be between 3 and 9 digits";
      }
    }

    return errors;
  };

  const handleCreateTeacher = () => {
    // Validate form before API call
    const validationErrors = validateForm();
    
    // Set field errors to show in form
    setFieldErrors(validationErrors);
    
    if (Object.keys(validationErrors).length > 0) {
      return; // Don't hit API if validation fails
    }

    const payload = {
      firstName: formData.firstName?.trim(),
      lastName: formData.lastName?.trim(),
      email: formData.email?.trim(),
      phoneNumber: formData.phoneNumber,
      gender: formData.gender || null,
      dob: formData.dob || null,
      role: formData.role || "teacher",
      departmentId: Number(formData.departmentId),
      qualification: formData.qualification || null,
      experienceYears: formData.experienceYears || null,
      designation: formData.designation || null,
      salary: formData.salary || null,
      joiningDate: formData.joiningDate || null,
      street: formData.street || null,
      city: formData.city || null,
      state: formData.state || null,
      country: formData.country || null,
      pinCode: formData.pinCode || null,
    };

    createTeacherMutation.mutate(payload);
  };

  // ========= API: Update Teacher =========
  const updateTeacherMutation = useMutation({
    mutationFn: async ({ id, body }) => {
      const res = await api.put("/institute/update-teacher", body, {
        params: { id },
      });
      return res.data;
    },
    onSuccess: async (data) => {
      closeCreateDialog();
      setSnackbar({
        open: true,
        message: data?.message || "Teacher updated successfully!",
        severity: "success",
      });
      await queryClient.invalidateQueries({
        queryKey: ["institute-teachers"],
        exact: false,
        refetchType: "all",
      });
      await refetchTeachers();
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message:
          error?.response?.data?.message ||
          "Failed to update teacher. Please try again.",
        severity: "error",
      });
    },
  });

  // const handleUpdateTeacher = () => {
  //   const rawTeacherId =
  //     editingTeacherId ||
  //     selectedTeacher?.teacherId ||
  //     selectedTeacher?.id ||
  //     null;
  //   const teacherId = rawTeacherId != null ? Number(rawTeacherId) : null;

  //   if (!teacherId || Number.isNaN(teacherId)) {
  //     setSnackbar({
  //       open: true,
  //       message:
  //         "Invalid Teacher ID. Please refresh the list and try again.",
  //       severity: "error",
  //     });
  //     return;
  //   }

  //   const body = {
  //     firstName: formData.firstName?.trim(),
  //     lastName: formData.lastName?.trim(),
  //     phoneNumber: formData.phoneNumber,
  //     departmentId: formData.departmentId || 1,
  //     // Optional fields (backend may ignore if not supported)
  //     qualification: formData.qualification || undefined,
  //     experienceYears: formData.experienceYears || undefined,
  //     designation: formData.designation || undefined,
  //     salary: formData.salary || undefined,
  //     joiningDate: formData.joiningDate || undefined,
  //     street: formData.street || undefined,
  //     city: formData.city || undefined,
  //     state: formData.state || undefined,
  //     country: formData.country || undefined,
  //     pinCode: formData.pinCode || undefined,
  //   };

  //   updateTeacherMutation.mutate({ id: teacherId, body });
  // };

  const handleUpdateTeacher = () => {
    const rawTeacherId =
      editingTeacherId ||
      selectedTeacher?.teacherId ||
      selectedTeacher?.id ||
      null;

    const teacherId = rawTeacherId != null ? Number(rawTeacherId) : null;

    if (!teacherId || Number.isNaN(teacherId)) {
      setSnackbar({
        open: true,
        message: "Invalid Teacher ID. Please refresh the list and try again.",
        severity: "error",
      });
      return;
    }

    // Validate form before API call
    const validationErrors = validateForm();
    
    // Set field errors to show in form
    setFieldErrors(validationErrors);
    
    if (Object.keys(validationErrors).length > 0) {
      return; // Don't hit API if validation fails
    }

    const body = {
      firstName: formData.firstName?.trim(),
      lastName: formData.lastName?.trim(),
      phoneNumber: formData.phoneNumber,

      gender: formData.gender || null,
      dob: formData.dob || null,
      role: formData.role || "teacher",

      departmentId: Number(formData.departmentId),

      qualification: formData.qualification || null,
      experienceYears: formData.experienceYears || null,
      designation: formData.designation || null,
      salary: formData.salary || null,
      joiningDate: formData.joiningDate || null,

      street: formData.street || null,
      city: formData.city || null,
      state: formData.state || null,
      country: formData.country || null,
      pinCode: formData.pinCode || null,
    };

    updateTeacherMutation.mutate({
      id: teacherId,
      body,
    });
  };

  // Block number keys in city/state/country (letters and spaces only)
  const blockNumberKeyDown = (e) => {
    if (/^[0-9]$/.test(e.key)) e.preventDefault();
  };
  const handleCityStateCountryChange = (field) => (e) => {
    const value = e.target.value.replace(/\d/g, "");
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // ========= API: Delete Teacher =========
  const deleteTeacherMutation = useMutation({
    mutationFn: async (teacherId) => {
      const res = await api.delete("/institute/delete-teacher", {
        params: { id: teacherId },
      });
      return res.data;
    },
    onSuccess: async (data) => {
      setSnackbar({
        open: true,
        message: data?.message || "Teacher deleted successfully!",
        severity: "success",
      });
      await queryClient.invalidateQueries({
        queryKey: ["institute-teachers"],
        exact: false,
        refetchType: "all",
      });
      await refetchTeachers();
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message:
          error?.response?.data?.message ||
          "Failed to delete teacher. Please try again.",
        severity: "error",
      });
    },
  });

  // ========= API: Update Teacher Status =========
  const updateTeacherStatusMutation = useMutation({
    mutationFn: async ({ id, status }) => {
      const res = await api.put(
        "/institute/teacher-status-update",
        { status }, // BODY
        { params: { id } } // QUERY PARAM
      );
      return res.data;
    },
    onMutate: async ({ id, status }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["institute-teachers"] });

      // Snapshot the previous value
      const previousTeachers = queryClient.getQueryData(["institute-teachers"]);

      // Optimistically update the cache
      queryClient.setQueryData(["institute-teachers"], (old) => {
        if (!old) return old;

        // Handle different response structures
        const normalized = old?.data?.teachers || old?.data?.rows || old?.data?.items || old?.data || old?.teachers || old?.rows || old?.items || old || [];
        const updated = normalized.map((t) => {
          const teacher = t.teacher || t.Teacher || t.teacherDetails || t;
          const teacherId = teacher?.id || t?.teacherId || t?.teacher_id || t?.id;

          if (teacherId === id) {
            // Update both the raw data structure and nested teacher object
            const updatedTeacher = teacher ? { ...teacher, status, isActive: status } : undefined;
            return {
              ...t,
              status: status, // Update boolean status
              isActive: status, // Also update isActive for consistency
              teacher: updatedTeacher,
              Teacher: updatedTeacher,
              teacherDetails: updatedTeacher,
            };
          }
          return t;
        });

        // Return in the same structure as the original
        if (old?.data) {
          if (old.data.teachers) return { ...old, data: { ...old.data, teachers: updated } };
          if (old.data.rows) return { ...old, data: { ...old.data, rows: updated } };
          if (old.data.items) return { ...old, data: { ...old.data, items: updated } };
          return { ...old, data: updated };
        }
        return updated;
      });

      // Return context with snapshot
      return { previousTeachers };
    },
    onSuccess: async (data, variables) => {
      // Update cache directly with response data before invalidating
      if (data?.data) {
        const updatedStatus = data.data.status;
        const teacherId = data.data.id || variables.id;

        queryClient.setQueryData(["institute-teachers"], (old) => {
          if (!old) return old;

          const normalized = old?.data?.teachers || old?.data?.rows || old?.data?.items || old?.data || old?.teachers || old?.rows || old?.items || old || [];
          const updated = normalized.map((t) => {
            const teacher = t.teacher || t.Teacher || t.teacherDetails || t;
            const tId = teacher?.id || t?.teacherId || t?.teacher_id || t?.id;

            if (tId === teacherId) {
              const updatedTeacher = teacher ? { ...teacher, status: updatedStatus, isActive: updatedStatus } : undefined;
              return {
                ...t,
                status: updatedStatus,
                isActive: updatedStatus,
                teacher: updatedTeacher,
                Teacher: updatedTeacher,
                teacherDetails: updatedTeacher,
              };
            }
            return t;
          });

          if (old?.data) {
            if (old.data.teachers) return { ...old, data: { ...old.data, teachers: updated } };
            if (old.data.rows) return { ...old, data: { ...old.data, rows: updated } };
            if (old.data.items) return { ...old, data: { ...old.data, items: updated } };
            return { ...old, data: updated };
          }
          return updated;
        });
      }

      setSnackbar({
        open: true,
        message: data?.message || "Teacher status updated successfully!",
        severity: "success",
      });

      // Invalidate and refetch to ensure data is in sync with server
      await queryClient.invalidateQueries({
        queryKey: ["institute-teachers"],
        exact: false,
        refetchType: "all",
      });
      await refetchTeachers();
    },
    onError: (error, variables, context) => {
      // Rollback to previous value on error
      if (context?.previousTeachers) {
        queryClient.setQueryData(["institute-teachers"], context.previousTeachers);
      }
      setSnackbar({
        open: true,
        message:
          error?.response?.data?.message ||
          "Failed to update teacher status. Please try again.",
        severity: "error",
      });
    },
  });

  const handleToggleStatus = (row, newStatus) => {
    const rawTeacherId = row?.teacherId || row?.id || null;
    const teacherId = rawTeacherId != null ? Number(rawTeacherId) : null;

    if (!teacherId || Number.isNaN(teacherId)) {
      setSnackbar({
        open: true,
        message: "Invalid Teacher ID. Please refresh and try again.",
        severity: "error",
      });
      return;
    }

    const rowId = String(teacherId);
    setTogglingTeacherId(rowId);
    setOptimisticStatus((prev) => ({ ...prev, [rowId]: newStatus }));
    updateTeacherStatusMutation.mutate(
      {
        id: teacherId,
        status: newStatus,
      },
      {
        onError: () => {
          setOptimisticStatus((prev) => {
            const next = { ...prev };
            delete next[rowId];
            return next;
          });
        },
        onSettled: () => {
          setTogglingTeacherId(null);
          setOptimisticStatus((prev) => {
            const next = { ...prev };
            delete next[rowId];
            return next;
          });
        },
      }
    );
  };

  const handleDeleteTeacher = (row) => {
    const teacherId = row?.teacherId || row?.id;
    if (!teacherId) return;
    setTeacherToDelete({
      teacherId,
      name: row?.name || "this teacher",
    });
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteTeacher = () => {
    const teacherId = teacherToDelete?.teacherId;
    if (!teacherId) return;
    deleteTeacherMutation.mutate(teacherId, {
      onSettled: () => {
        setDeleteConfirmOpen(false);
        setTeacherToDelete(null);
      },
    });
  };

  // Handle export functionality
  const handleExport = async () => {
    try {
      const path = "/export/teachers-csv";
      const response = await api.post(path);

      if (response.status !== 200) {
        throw new Error('Export failed');
      }

      // Handle CSV data from response
      const csvData = response.data.csvData;
      if (!csvData) {
        throw new Error('No CSV data received');
      }

      // Create blob from CSV string
      const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
      const fileUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = fileUrl;
      a.download = 'teachers.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(fileUrl);
      document.body.removeChild(a);

      // Show success message
      setSnackbar({
        open: true,
        message: `Export successful! ${response.data.totalRecords || 0} teachers exported.`,
        severity: "success",
      });
    } catch (error) {
      console.error("Export error:", error);
      setSnackbar({
        open: true,
        message: "Failed to export teachers. Please try again.",
        severity: "error",
      });
    }
  };

  const openEditTeacher =async (row) => {
    
    const rawTeacherId = row?.teacherId || row?.id || null;
    const teacherId = rawTeacherId != null ? Number(rawTeacherId) : null;
    setIsEditMode(true);
    setEditingTeacherId(teacherId);
    setFieldErrors({}); // Clear field errors when opening dialog
    
    setFormData((prev) => ({
      ...prev,
      firstName: row?.firstName || "",
      lastName: row?.lastName || "",
      email: row?.email || "",
      gender: row?.gender || "",
      phoneNumber: row?.phone || row?.phoneNumber || "",
      departmentId: row?.departmentId ? String(row.departmentId) : "", // Ensure this is a string
      qualification: row?.qualification || "",
      experienceYears: row?.experienceYears || "",
      designation: row?.designation || "",
      salary: row?.salary || "",
      joiningDate: row?.joiningDate || "",
      dob: formatDateForInput(row?.dob),  
      street: row?.street || "",
      city: row?.city || "",
      state: row?.state || "",
      country: row?.country || "",
      pinCode: row?.pinCode || "",
    }));
    setCreateDialogOpen(true);

    try {
      setLoadingDepartments(true);
      const effectiveInstituteId = authUser?.instituteId ?? currentUser?.instituteId ?? selectedInstitute?.id;
      const params = effectiveInstituteId ? { instituteId: effectiveInstituteId } : {};
      const response = await api.get('/institute/get-departments-names', { params });
      setDepartments(response.data.data || []);
    } catch (error) {
      console.error('Error fetching departments:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load departments',
        severity: 'error'
      });
    } finally {
      setLoadingDepartments(false);
    }
  };

  const openCreateTeacher = (targetRole = "teacher") => {
    setIsEditMode(false);
    setEditingTeacherId(null);
    // Accept any valid role, default to "teacher" if not provided or invalid
    // Use available roles from the dropdown, with fallback to core roles
    const validRoles = availableRoles.length > 0 
      ? availableRoles.map(role => role.slug?.toLowerCase()).filter(Boolean)
      : ["teacher", "admin", "hod"]; // Fallback to core roles
    
    const normalizedTargetRole = validRoles.includes(String(targetRole || "teacher").toLowerCase()) 
      ? String(targetRole || "teacher").toLowerCase() 
      : "teacher";
    setFormData({ ...getEmptyFormData(), role: normalizedTargetRole });
    setFieldErrors({}); // Clear field errors when opening dialog
    // Open popup immediately; fetch departments in background.
    setCreateDialogOpen(true);
    setLoadingDepartments(true);
    const effectiveInstituteId =
      authUser?.instituteId ?? currentUser?.instituteId ?? selectedInstitute?.id;
    const params = effectiveInstituteId ? { instituteId: effectiveInstituteId } : {};
    api
      .get('/institute/get-departments-names', { params })
      .then((response) => {
        setDepartments(response.data.data || []);
      })
      .catch((error) => {
        console.error('Error fetching departments:', error);
        setSnackbar({
          open: true,
          message: 'Failed to load departments',
          severity: 'error',
        });
      })
      .finally(() => {
        setLoadingDepartments(false);
      });
  };

  const closeCreateDialog = () => {
    setCreateDialogOpen(false);
    setIsEditMode(false);
    setEditingTeacherId(null);
    setFieldErrors({});
    if (typeof onParentClose === "function") {
      onParentClose();
    }
  };

  useEffect(() => {
    if (!openFromParent) return;
    openCreateTeacher(parentRole === "hod" ? "hod" : "teacher");
  }, [openFromParent, parentRole]);

  useEffect(() => {
    if (!openEditFromParent) return;
    if (parentPrefillLoading) {
      setIsEditMode(true);
      setCreateDialogOpen(true);
      setEditingTeacherId(null);
      setFieldErrors({});
      return;
    }
    if (!parentEditData) return;
    openEditTeacher(parentEditData);
  }, [openEditFromParent, parentEditData, parentPrefillLoading]);

  useEffect(() => {
    if (!location.state?.openCreateFromUsers) return;

    const requestedRole = String(location.state?.preselectedRole || "teacher").toLowerCase();
    openCreateTeacher(requestedRole === "hod" ? "hod" : "teacher");

    // Clear one-time navigation state so dialog doesn't reopen on refresh/back.
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, navigate]);

  const columns = [
    {
      field: "name",
      headerName: "Teacher",
      flex: 1,
      minWidth: 200,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%", alignSelf: "center" }}>
          <Avatar sx={{ width: 36, height: 36, flexShrink: 0 }}>{params.row.name[0]}</Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" fontWeight={500} noWrap>
              {params.row.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" noWrap>
              {params.row.designation}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: "email",
      headerName: "Contact",
      width: 250,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, alignItems: "flex-start" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
            <Email sx={{ fontSize: 14, color: "text.secondary", flexShrink: 0 }} />
            <Typography variant="caption" noWrap sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>{params.row.email}</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
            <Phone sx={{ fontSize: 14, color: "text.secondary", flexShrink: 0 }} />
            <Typography variant="caption" noWrap>{params.row.phone}</Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: "subjects",
      headerName: "Subjects",
      flex: 1,
      minWidth: 200,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        const subjects = params.row.subjects || [];
        if (subjects.length === 0) return <Typography variant="body2" color="text.secondary">-</Typography>;

        const getSubjectNames = () => {
          if (typeof subjects[0] === "string") return subjects;
          if (typeof subjects[0] === "object") {
            return subjects.map((s) => s.subjectName ?? s.name ?? s.subject ?? "-");
          }
          return [];
        };
        const names = getSubjectNames();
        if (names.length === 0) return <Typography variant="body2" color="text.secondary">-</Typography>;

        const chipBoxSx = {
          display: "flex",
          flexWrap: "wrap",
          gap: "6px 8px",
          alignItems: "center",
          alignContent: "flex-start",
          lineHeight: 1.5,
          py: 0.25,
          width: "100%",
        };

        if (names.length === 1) {
          return (
            <Box sx={chipBoxSx}>
              <Chip label={names[0]} size="small" sx={{ flexShrink: 0, margin: 0 }} />
            </Box>
          );
        }

        const handleShowAll = (e) => {
          e.stopPropagation();
          setSubjectsDialogList(names);
          setSubjectsDialogOpen(true);
        };

        return (
          <Box sx={chipBoxSx}>
            <Chip label={names[0]} size="small" sx={{ flexShrink: 0, margin: 0 }} />
            <Chip
              label={`+${names.length - 1}`}
              size="small"
              onClick={handleShowAll}
              sx={{
                flexShrink: 0,
                margin: 0,
                cursor: "pointer",
                "&:hover": { opacity: 0.9 },
              }}
              color="primary"
              variant="outlined"
            />
          </Box>
        );
      },
    },
    {
      field: "classes",
      headerName: "Classes",
      flex: 1,
      minWidth: 200,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        const classes = params.row.classes || [];
        if (classes.length === 0) return <Typography variant="body2" color="text.secondary">-</Typography>;

        const chipBoxSx = {
          display: "flex",
          flexWrap: "wrap",
          gap: "6px 8px",
          alignItems: "flex-start",
          alignContent: "flex-start",
          lineHeight: 1.5,
          py: 0.25,
          width: "100%",
        };
        const chipSx = { flexShrink: 0, margin: 0 };

        // If classes is an array of strings
        if (typeof classes[0] === "string") {
          return (
            <Box sx={chipBoxSx}>
              {classes.map((cls, i) => (
                <Chip key={i} label={cls} size="small" variant="outlined" color="primary" sx={chipSx} />
              ))}
            </Box>
          );
        }

        // If classes is an array of objects with classId and className
        if (typeof classes[0] === "object" && classes[0].className) {
          // Use displayName if available, otherwise combine className and classSection
          const classDisplayNames = classes.map((c) => 
            c.displayName || (c.classSection ? `${c.className} - ${c.classSection}` : c.className)
          );
          return (
            <Box sx={chipBoxSx}>
              {classDisplayNames.map((cls, i) => (
                <Chip key={i} label={cls} size="small" variant="outlined" color="primary" sx={chipSx} />
              ))}
            </Box>
          );
        }

        return <Typography variant="body2" color="text.secondary">-</Typography>;
      },
    },
    {
      field: "status",
      headerName: "Status",
      width: 100,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const rawId = params.row?.teacherId ?? params.row?.id;
        const rowId = rawId != null ? String(rawId) : null;
        if (rowId == null) return null;

        const isActiveValue = params.row?.isActive;
        const statusValue = params.value;
        let resolvedActive = false;
        if (typeof isActiveValue === "boolean") resolvedActive = isActiveValue;
        else if (typeof statusValue === "boolean") resolvedActive = statusValue;
        else if (typeof statusValue === "string") resolvedActive = statusValue.toLowerCase() === "active";

        const isActive = optimisticStatus[rowId] ?? resolvedActive;

        return (
          <Box sx={{ alignSelf: "center" }} key={rowId}>
            <Switch
              key={rowId}
              checked={Boolean(isActive)}
              onChange={(e) => {
                e.stopPropagation();
                handleToggleStatus(params.row, !isActive);
              }}
              size="small"
              color="primary"
              disabled={togglingTeacherId === rowId}
            />
          </Box>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 140,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0, alignSelf: "center" }}>
          <IconButton
            size="small"
            onClick={() => {
              setSelectedTeacher(params.row);
              setViewDialogOpen(true);
            }}
          >
            <Visibility />
          </IconButton>
          <PermissionGate permission="teacher.profile.update">
            <IconButton size="small" onClick={() => openEditTeacher(params.row)}>
              <Edit />
            </IconButton>
          </PermissionGate>
          <PermissionGate permission="teacher.profile.delete">
            <IconButton size="small" color="error">
              <Delete onClick={() => handleDeleteTeacher(params.row)} />
            </IconButton>
          </PermissionGate>
        </Box>
      ),
    },
  ];

  // Filter by status tab only (search is handled by API)
  const filteredTeachers = useMemo(() => {
    if (activeTab === 0) return teachers;
    if (activeTab === 1) return teachers.filter((t) => t.status === "active");
    if (activeTab === 2) return teachers.filter((t) => t.status === "inactive");
    return teachers;
  }, [teachers, activeTab]);

  return (
    <Box sx={{ py: 0, px: 0 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={600} sx={{ lineHeight: 1.3 }}>
            Teacher Management
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Manage teachers in your department
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
          <PermissionGate permission="user.import">
            <Button
              variant="outlined"
              startIcon={<Upload />}
              onClick={() => setImportDialogOpen(true)}
            >
              Import
            </Button>
          </PermissionGate>
          <PermissionGate permission="user.export">
            <Tooltip title="Exported files have masked personal information. Contact support if you need the full personal information.">
              <span>
                <Button
                  variant="outlined"
                  startIcon={<Download />}
                  onClick={handleExport}
                  disabled={filteredTeachers.length === 0}
                >
                  Export
                </Button>
              </span>
            </Tooltip>
          </PermissionGate>
          <PermissionGate permission="teacher.profile.create">
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={openCreateTeacher}
            >
              Add Teacher
            </Button>
          </PermissionGate>
        </Box>
      </Box>

      <Card>
        <CardContent>
          <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap", alignItems: "center" }}>
            <TextField
              placeholder="Search teachers..."
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ width: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
            <DepartmentModuleFilterSelect
              labelId="teachers-department-filter"
              value={departmentFilter}
              onChange={setDepartmentFilter}
              instituteId={effectiveInstituteId}
              showAllOption={true}
            />
            <StatusDropdown
              label="Status"
              value={activeTab}
              onChange={setActiveTab}
              options={[
                { label: "All", value: 0 },
                { label: "Active", value: 1 },
                { label: "Inactive", value: 2 },
              ]}
              size="small"
              sx={{ minWidth: 160 }}
            />
          </Box>

          <DataGrid
            rows={filteredTeachers}
            columns={columns}
            autoHeight
            disableRowSelectionOnClick
            getRowHeight={() => "auto"}
            paginationMode="server"
            // We filter Active/Inactive client-side, so the displayed count must follow the filtered rows.
            rowCount={activeTab === 0 ? (paginationInfo.total || filteredTeachers.length) : filteredTeachers.length}
            paginationModel={paginationModel}
            onPaginationModelChange={(model) => {
              setPaginationModel(model);
              setPagination((prev)=>({ ...prev, page: model.page + 1, limit: model.pageSize }));
            }}
            pageSizeOptions={PAGINATION_PAGE_SIZE_OPTIONS}
            loading={teachersLoading || isRefetching}
            sx={{
              border: "none",
              "& .MuiDataGrid-row": { maxHeight: "none !important" },
              "& .MuiDataGrid-cell": {
                overflow: "visible",
                alignItems: "flex-start",
                paddingTop: 1,
                paddingBottom: 1,
              },
              "& .MuiDataGrid-cell--withRenderer": { overflow: "visible" },
            }}
          />
        </CardContent>
      </Card>

      {/* Subjects list dialog (when user clicks +N) */}
      <Dialog
        open={subjectsDialogOpen}
        onClose={(e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          setSubjectsDialogOpen(false);
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ borderBottom: 1, borderColor: "divider", pb: 1.5 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6" fontWeight={600}>
              Subjects
            </Typography>
            <Typography
              component="button"
              variant="body2"
              color="primary"
              onClick={() => setSubjectsDialogOpen(false)}
              sx={{
                background: "none",
                border: "none",
                cursor: "pointer",
                p: 0,
                "&:hover": { textDecoration: "underline" },
              }}
            >
              Close
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              alignItems: "flex-start",
              alignContent: "flex-start",
              my: 1
            }}
          >
            {subjectsDialogList.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No subjects
              </Typography>
            ) : (
              subjectsDialogList.map((name, i) => (
                <Chip key={i} label={name} size="small" sx={{ flexShrink: 0 }} />
              ))
            )}
          </Box>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={(e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          setViewDialogOpen(false);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6" fontWeight={600}>
              Teacher Details
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {canRevealTeacherPii && hasMaskedTeacherPii(selectedTeacher) && (
                <Tooltip title="Reveal personal information">
                  <span>
                    <IconButton
                      size="small"
                      aria-label="reveal personal information"
                      disabled={revealingTeacherPii}
                      onClick={() => handleRevealTeacherPii(selectedTeacher?.id)}
                    >
                      {revealingTeacherPii ? (
                        <CircularProgress size={18} />
                      ) : (
                        <Visibility fontSize="small" />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              <IconButton onClick={() => setViewDialogOpen(false)}>
                <Close />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ maxHeight: "70vh", overflowY: "auto", px: 3, pt: 2, pb: 3 }}>
          {selectedTeacher && (
            <Box>
              {/* Header Section */}
              <Box sx={{ textAlign: "center", pb: 2.5, mb: 2.5, borderBottom: "1px solid", borderColor: "divider" }}>
                <Avatar sx={{ width: 80, height: 80, mx: "auto", mb: 1.5, bgcolor: "primary.main" }}>
                  {selectedTeacher.name?.[0]?.toUpperCase() || "T"}
                </Avatar>
                <Typography variant="h6" fontWeight={600}>
                  {selectedTeacher.name || `${selectedTeacher.firstName || ""} ${selectedTeacher.lastName || ""}`.trim() || "N/A"}
                </Typography>
                {selectedTeacher.designation && (
                  <Chip
                    label={selectedTeacher.designation}
                    size="small"
                    color="primary"
                    sx={{ mt: 1 }}
                  />
                )}
              </Box>

              {/* Basic Information */}
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5, mt: 0 }}>
                Basic Information
              </Typography>
              <Grid container spacing={2} sx={{ mb: 2.5 }}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    First Name
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {selectedTeacher.firstName || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Last Name
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {selectedTeacher.lastName || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Email
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {selectedTeacher.email || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Phone Number
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {selectedTeacher.phone || selectedTeacher.phoneNumber || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Gender
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {selectedTeacher.gender ? selectedTeacher.gender.charAt(0).toUpperCase() + selectedTeacher.gender.slice(1) : "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Date of Birth
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {selectedTeacher.dob ? displayDate(selectedTeacher.dob, "N/A") : "N/A"}
                  </Typography>
                </Grid>
              </Grid>

              {/* Professional Information */}
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5, mt: 2.5 }}>
                Professional Information
              </Typography>
              <Grid container spacing={2} sx={{ mb: 2.5 }}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Role
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {selectedTeacher.role ? selectedTeacher.role.toUpperCase() : "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Teacher Login ID
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {selectedTeacher.teacherLoginID || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Qualification
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {selectedTeacher.qualification || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Experience (Years)
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {selectedTeacher.experienceYears !== null && selectedTeacher.experienceYears !== undefined 
                      ? `${selectedTeacher.experienceYears} years` 
                      : "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Designation
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {selectedTeacher.designation || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Salary
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {selectedTeacher.salary ? formatSalary(selectedTeacher.salary) : "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Joining Date
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {selectedTeacher.joiningDate || selectedTeacher.joinDate || "N/A"}
                  </Typography>
                </Grid>
              </Grid>

              {/* Department Information */}
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5, mt: 2.5 }}>
                Department Information
              </Typography>
              <Grid container spacing={2} sx={{ mb: 2.5 }}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Department ID
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {selectedTeacher.departmentId || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Department Name
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {selectedTeacher.departmentName || "N/A"}
                  </Typography>
                </Grid>
              </Grid>

              {/* Address Information */}
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5, mt: 2.5 }}>
                Address Information
              </Typography>
              <Grid container spacing={2} sx={{ mb: 2.5 }}>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Full Address
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {selectedTeacher.address || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Street
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {selectedTeacher.street || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    City
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {selectedTeacher.city || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    State
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {selectedTeacher.state || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Country
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {selectedTeacher.country || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Pin Code
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {selectedTeacher.pinCode || "N/A"}
                  </Typography>
                </Grid>
              </Grid>

              {/* Classes and Subjects */}
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5, mt: 2.5 }}>
                Classes & Subjects
              </Typography>
              <Grid container spacing={2} sx={{ mb: 2.5 }}>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Classes
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    {selectedTeacher.classes && selectedTeacher.classes.length > 0 ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selectedTeacher.classes.map((cls, i) => {
                          // Handle both string and object formats, including section/division
                          const classDisplayName = typeof cls === 'string' 
                            ? cls 
                            : (cls.displayName || (cls.classSection ? `${cls.className} - ${cls.classSection}` : cls.className));
                          return (
                            <Chip
                              key={i}
                              label={classDisplayName}
                              size="small"
                              variant="outlined"
                              color="primary"
                              sx={{ mr: 0.5, mb: 0.5 }}
                            />
                          );
                        })}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">No classes assigned</Typography>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Subjects
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    {selectedTeacher.subjects && selectedTeacher.subjects.length > 0 ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selectedTeacher.subjects.map((subj, i) => {
                          const subjectName = typeof subj === 'string' ? subj : subj.subjectName;
                          return (
                            <Chip
                              key={i}
                              label={subjectName}
                              size="small"
                              sx={{ mr: 0.5, mb: 0.5 }}
                            />
                          );
                        })}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">No subjects assigned</Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>

              {/* Status and Metadata */}
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5, mt: 2.5 }}>
                Status & Metadata
              </Typography>
              <Grid container spacing={2} sx={{ mb: 2.5 }}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Status
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip
                      label={selectedTeacher.isActive || selectedTeacher.status === true || selectedTeacher.status === "active" ? "Active" : "Inactive"}
                      size="small"
                      color={selectedTeacher.isActive || selectedTeacher.status === true || selectedTeacher.status === "active" ? "success" : "default"}
                    />
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Can Delete
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {selectedTeacher.canDelete !== undefined ? (selectedTeacher.canDelete ? "Yes" : "No") : "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Created At
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {selectedTeacher.createdAt ? new Date(selectedTeacher.createdAt).toLocaleString() : "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Updated At
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {selectedTeacher.updatedAt ? new Date(selectedTeacher.updatedAt).toLocaleString() : "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    UUID
                  </Typography>
                  <Typography variant="body2" fontWeight={500} sx={{ wordBreak: "break-all" }}>
                    {selectedTeacher.uuid || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Teacher ID
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {selectedTeacher.teacherId || selectedTeacher.id || "N/A"}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          <Button
            variant="contained"
            onClick={() => {
              setViewDialogOpen(false);
              openEditTeacher(selectedTeacher);
            }}
          >
            Edit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={(e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          closeCreateDialog();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            {isEditMode ? "Edit Teacher" : "Add New Teacher"}
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {isEditMode && canRevealTeacherPii && hasMaskedTeacherPii(formData) && (
                <Tooltip title="Reveal personal information">
                  <span>
                    <IconButton
                      size="small"
                      aria-label="reveal personal information"
                      disabled={revealingTeacherPii}
                      onClick={() => handleRevealTeacherPii(editingTeacherId || formData?.id)}
                    >
                      {revealingTeacherPii ? (
                        <CircularProgress size={18} />
                      ) : (
                        <Visibility fontSize="small" />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              <IconButton onClick={closeCreateDialog}>
                <Close />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        {/* <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Full Name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Phone"
                value={formData.phone}
                onChange={(e) =>
                  setFormData({ ...formData, phone: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Designation</InputLabel>
                <Select
                  value={formData.designation}
                  label="Designation"
                  onChange={(e) =>
                    setFormData({ ...formData, designation: e.target.value })
                  }
                >
                  <MenuItem value="Professor">Professor</MenuItem>
                  <MenuItem value="Associate Professor">
                    Associate Professor
                  </MenuItem>
                  <MenuItem value="Assistant Professor">
                    Assistant Professor
                  </MenuItem>
                  <MenuItem value="Lecturer">Lecturer</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent> */}

        <DialogContent>
          {isEditMode && parentPrefillLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 8 }}>
              <CircularProgress />
            </Box>
          ) : (
          <Grid container spacing={2} sx={{ mt: 1 }}>

            {/* FIRST NAME */}
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="First Name *"
                value={formData.firstName}
                inputProps={{ maxLength: 20 }}
                onChange={(e) => {
                  // Only allow letters, no spaces or numbers
                  const value = e.target.value.replace(/[^A-Za-z]/g, "");
                  if (value.length <= 20) {
                    setFormData({ ...formData, firstName: value });
                    // Clear error when user types
                    if (fieldErrors.firstName) {
                      setFieldErrors({ ...fieldErrors, firstName: undefined });
                    }
                  }
                }}
                helperText={
                  fieldErrors.firstName ||
                  (formData.firstName.length > 0 && formData.firstName.length < 3
                    ? "Minimum 3 characters required"
                    : formData.firstName.length > 0 && formData.firstName.length > 20
                    ? "Cannot exceed 20 characters"
                    : formData.firstName.length > 0 && !/^[A-Za-z]+$/.test(formData.firstName)
                    ? "Only letters allowed (no spaces or numbers)"
                    : "")
                }
                error={
                  !!fieldErrors.firstName ||
                  (formData.firstName.length > 0 && formData.firstName.length < 3) ||
                  (formData.firstName.length > 0 && formData.firstName.length > 20) ||
                  (formData.firstName.length > 0 && !/^[A-Za-z]+$/.test(formData.firstName))
                }
              />

            </Grid>

            {/* LAST NAME */}
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={formData.lastName}
                inputProps={{ maxLength: 20 }}
                onChange={(e) => {
                  // Only allow letters, no spaces or numbers
                  const value = e.target.value.replace(/[^A-Za-z]/g, "");
                  if (value.length <= 20) {
                    setFormData({ ...formData, lastName: value });
                    // Clear error when user types
                    if (fieldErrors.lastName) {
                      setFieldErrors({ ...fieldErrors, lastName: undefined });
                    }
                  }
                }}
                helperText={
                  fieldErrors.lastName ||
                  (formData.lastName.length > 0 && formData.lastName.length < 3
                    ? "Minimum 3 characters required"
                    : formData.lastName.length > 0 && formData.lastName.length > 20
                    ? "Cannot exceed 20 characters"
                    : formData.lastName.length > 0 && !/^[A-Za-z]+$/.test(formData.lastName)
                    ? "Only letters allowed (no spaces or numbers)"
                    : "")
                }
                error={
                  !!fieldErrors.lastName ||
                  (formData.lastName.length > 0 && formData.lastName.length < 3) ||
                  (formData.lastName.length > 0 && formData.lastName.length > 20) ||
                  (formData.lastName.length > 0 && !/^[A-Za-z]+$/.test(formData.lastName))
                }
              />

            </Grid>

            {/* EMAIL */}
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Email *"
                type="email"
                value={formData.email}
                disabled={isEditMode}
                onChange={(e) => {
                  setFormData({ ...formData, email: e.target.value });
                  // Clear error when user types
                  if (fieldErrors.email) {
                    setFieldErrors({ ...fieldErrors, email: undefined });
                  }
                }}
                error={
                  !!fieldErrors.email ||
                  (formData.email !== "" &&
                  !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
                }
                helperText={
                  fieldErrors.email ||
                  (formData.email !== "" &&
                    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)
                    ? "Please enter a valid email address"
                    : "")
                }
              />

            </Grid>

            {/* DOB */}
            <Grid item xs={6}>
              <DatePicker
                label="Date of Birth *"
                format={DATE_DISPLAY_FORMAT}
                value={formData.dob ? new Date(formData.dob) : null}
                onChange={(val) => {
                  const next = toApiDate(val);
                  setFormData((prev) => ({ ...prev, dob: next }));
                  if (fieldErrors.dob) setFieldErrors((prev) => ({ ...prev, dob: undefined }));
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                    error: !!fieldErrors.dob,
                    helperText: fieldErrors.dob,
                  },
                }}
              />
            </Grid>

            {/* PHONE */}
            <Grid item xs={6}>
              <FormControl
                fullWidth
                variant="outlined"
                error={!!fieldErrors.phoneNumber}
              >
                <InputLabel sx={{ top: '-5px' }} shrink htmlFor="phoneNumber">
                  Phone *
                </InputLabel>
                <PhoneInput
                  country="in"
                  specialLabel=""
                  inputProps={{
                    id: 'phoneNumber',
                    name: 'phoneNumber',
                    'aria-label': 'Phone'
                  }}
                  countryCodeEditable={false}
                  enableSearch
                  value={formData.phoneNumber || ""}
                  onChange={(value) => {
                    setFormData((prev) => ({ ...prev, phoneNumber: value }));
                    // Real-time validation (same as Profile.js): show "Invalid phone number" as user types
                    if (!value || value.trim() === "") {
                      setFieldErrors((prev) => ({ ...prev, phoneNumber: undefined }));
                    } else {
                      const digitsOnly = (value || "").replace(/\D/g, "");
                      setFieldErrors((prev) => ({
                        ...prev,
                        phoneNumber: digitsOnly.length < 10 ? "Invalid phone number" : undefined,
                      }));
                    }
                  }}
                  containerStyle={{
                    marginTop: '4px'
                  }}
                  inputStyle={{
                    width: '100%',
                    height: '56px',
                    paddingLeft: '48px',
                    borderRadius: '4px',
                    border: fieldErrors.phoneNumber
                      ? '1px solid #d32f2f'
                      : '1px solid rgba(0, 0, 0, 0.23)'
                  }}
                />
                {fieldErrors.phoneNumber && (
                  <FormHelperText>
                    {fieldErrors.phoneNumber}
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>

            {/* GENDER */}
            <Grid item xs={6}>
              <FormControl fullWidth error={!!fieldErrors.gender}>
                <InputLabel>Gender *</InputLabel>
                <Select
                  value={formData.gender}
                  label="Gender"
                  onChange={(e) => {
                    setFormData({ ...formData, gender: e.target.value });
                    // Clear error when user selects
                    if (fieldErrors.gender) {
                      setFieldErrors({ ...fieldErrors, gender: undefined });
                    }
                  }}
                >
                  <MenuItem value="male">Male</MenuItem>
                  <MenuItem value="female">Female</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
                {fieldErrors.gender && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                    {fieldErrors.gender}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {/* ROLE */}
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  value={formData.role}
                  label="Role"
                  onChange={(e) =>
                    setFormData({ ...formData, role: e.target.value })
                  }
                >
                  {availableRoles.map((role) => (
                    <MenuItem key={role.slug} value={role.slug}>
                      {role.name || role.slug.charAt(0).toUpperCase() + role.slug.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* QUALIFICATION */}
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Qualification *"
                value={formData.qualification}
                inputProps={{ maxLength: 30 }}
                onChange={(e) => {
                  setFormData({ ...formData, qualification: e.target.value });
                  // Clear error when user types
                  if (fieldErrors.qualification) {
                    setFieldErrors({ ...fieldErrors, qualification: undefined });
                  }
                }}
                error={!!fieldErrors.qualification}
                helperText={fieldErrors.qualification}
              />
            </Grid>

            {/* EXPERIENCE */}
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label="Experience (Years) *"
                value={formData.experienceYears}
                onChange={(e) => {
                  // Only update if the value is empty or has 1-2 digits
                  if (e.target.value === '' || /^\d{1,2}$/.test(e.target.value)) {
                    setFormData({ ...formData, experienceYears: e.target.value });
                    // Clear error when user types
                    if (fieldErrors.experienceYears) {
                      setFieldErrors({ ...fieldErrors, experienceYears: undefined });
                    }
                  }
                }}
                onKeyDown={(e) => {
                  // Prevent typing more than 2 digits
                  if (e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab' && 
                      (e.target.value?.length >= 2 || !/^\d$/.test(e.key))) {
                    e.preventDefault();
                  }
                }}
                inputProps={{
                  maxLength: 2,
                  inputMode: 'numeric',
                  pattern: '\d*',
                }}
                error={!!fieldErrors.experienceYears}
                helperText={fieldErrors.experienceYears}
              />
            </Grid>

            {/* DESIGNATION */}
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Designation *"
                value={formData.designation}
                inputProps={{ maxLength: 30 }}
                onChange={(e) => {
                  setFormData({ ...formData, designation: e.target.value });
                  // Clear error when user types
                  if (fieldErrors.designation) {
                    setFieldErrors({ ...fieldErrors, designation: undefined });
                  }
                }}
                error={!!fieldErrors.designation}
                helperText={fieldErrors.designation}
              />
            </Grid>


            {/* SALARY */}
<Grid item xs={6}>
  <TextField
    fullWidth
    label="Salary *"
    value={formData.salary ? formatSalary(formData.salary) : ''}
    onChange={(e) => {
      // Extract only numbers from input
      let rawValue = e.target.value.replace(/[^0-9]/g, '');

      // Limit salary to max 999999
      if (rawValue > 999999) return;

      setFormData({ ...formData, salary: rawValue });

      // Clear error when user types
      if (fieldErrors.salary) {
        setFieldErrors({ ...fieldErrors, salary: undefined });
      }
    }}
    InputProps={{
      startAdornment: (
        <InputAdornment position="start">
          ₹
        </InputAdornment>
      ),
    }}
    error={!!fieldErrors.salary}
    helperText={fieldErrors.salary}
  />
</Grid>


            {/* DEPARTMENT */}
            <Grid item xs={6}>
              <FormControl fullWidth error={!!fieldErrors.departmentId}>
                <InputLabel>Department *</InputLabel>
                <Select
                  value={formData.departmentId}
                  label="Department"
                  onChange={(e) => {
                    setFormData({ ...formData, departmentId: e.target.value });
                    // Clear error when user selects
                    if (fieldErrors.departmentId) {
                      setFieldErrors({ ...fieldErrors, departmentId: undefined });
                    }
                  }}
                  disabled={loadingDepartments}
                >
                  <MenuItem value="">Select Department</MenuItem>
                  {departments.map((dept) => (
                    <MenuItem key={dept.id} value={dept.id}>
                      {dept.name}
                    </MenuItem>
                  ))}
                  {loadingDepartments && (
                    <MenuItem disabled>Loading departments...</MenuItem>
                  )}
                </Select>
                {fieldErrors.departmentId && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                    {fieldErrors.departmentId}
                  </Typography>
                )}
              </FormControl>
            </Grid>



            {/* JOINING DATE */}
            <Grid item xs={6}>
              <DatePicker
                label="Joining Date *"
                format={DATE_DISPLAY_FORMAT}
                value={formData.joiningDate ? new Date(formData.joiningDate) : null}
                onChange={(val) => {
                  const next = toApiDate(val);
                  setFormData((prev) => ({ ...prev, joiningDate: next }));
                  if (fieldErrors.joiningDate)
                    setFieldErrors((prev) => ({ ...prev, joiningDate: undefined }));
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                    error: !!fieldErrors.joiningDate,
                    helperText: fieldErrors.joiningDate,
                  },
                }}
              />
            </Grid>

            {/* Street - Google Places Autocomplete */}
            <Grid item xs={12}>
              <AddressAutocomplete
                onAddressSelect={(address) => {
                  console.log("Address selected:", address);
                  setFormData({
                    ...formData,
                    street: address.street || "",
                    city: address.city || "",
                    state: address.state || "",
                    country: address.country || "",
                    pinCode: address.pinCode || "",
                  });
                }}
                placeholder="Search for an address (e.g., Chandigarh, Delhi)"
                countryRestriction="in"
              />
            </Grid>

            {/* ADDRESS FIELDS - Auto-filled by AddressAutocomplete */}
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Street"
                value={formData.street || ""}
                onChange={(e) =>
                  setFormData({ ...formData, street: e.target.value })
                }
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="City"
                value={formData.city || ""}
                onChange={handleCityStateCountryChange("city")}
                onKeyDown={blockNumberKeyDown}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="State"
                value={formData.state || ""}
                onChange={handleCityStateCountryChange("state")}
                onKeyDown={blockNumberKeyDown}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Country"
                value={formData.country || ""}
                onChange={handleCityStateCountryChange("country")}
                onKeyDown={blockNumberKeyDown}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Pincode"
                value={formData.pinCode || ""}
                onChange={(e) => {
                  // Only allow numbers, no alphabets
                  const value = e.target.value.replace(/[^0-9]/g, "");
                  setFormData({ ...formData, pinCode: value });
                  // Clear error when user types
                  if (fieldErrors.pinCode) {
                    setFieldErrors({ ...fieldErrors, pinCode: undefined });
                  }
                }}
                inputProps={{ maxLength: 9 }}
                helperText={
                  fieldErrors.pinCode ||
                  (formData.pinCode && formData.pinCode.length > 0 && !/^\d+$/.test(formData.pinCode)
                    ? "Pincode must contain only numbers (no alphabets)"
                    : formData.pinCode && (formData.pinCode.length < 3 || formData.pinCode.length > 9)
                    ? "Pincode must be between 3 and 9 digits"
                    : "")
                }
                error={
                  !!fieldErrors.pinCode ||
                  (formData.pinCode && formData.pinCode.length > 0 && !/^\d+$/.test(formData.pinCode)) ||
                  (formData.pinCode && (formData.pinCode.length < 3 || formData.pinCode.length > 9))
                }
              />
              {/* <TextField
                fullWidth
                label="Pincode"
                value={formData.pinCode || ""}
                inputProps={{ inputMode: "numeric", maxLength: 9 }}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, "");
                  setFormData({ ...formData, pinCode: v });
                }}
                error={
                  formData.pinCode &&
                  !/^\d{3,9}$/.test(formData.pinCode)
                }
                helperText={
                  formData.pinCode &&
                  (!/^\d{3,9}$/.test(formData.pinCode)
                    ? "Pincode must be 3–9 digits"
                    : "")
                }
              /> */}


            </Grid>

          </Grid>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={closeCreateDialog}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={isEditMode ? handleUpdateTeacher : handleCreateTeacher}
            disabled={parentPrefillLoading}
            disabled={
              createTeacherMutation.isPending || updateTeacherMutation.isPending
            }
            startIcon={
              (createTeacherMutation.isPending || updateTeacherMutation.isPending) ? (
                <CircularProgress size={20} color="inherit" />
              ) : null
            }
          >
            {createTeacherMutation.isPending
              ? "Creating..."
              : updateTeacherMutation.isPending
              ? "Updating..."
              : isEditMode
              ? "Update Teacher"
              : "Add Teacher"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Wizard */}
      <DataImportWizard
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        entityType="teachers"
        instituteId={currentUser?.instituteId}
        onImportComplete={() => queryClient.invalidateQueries({ queryKey: ["institute-teachers"] })}
      />

      {/* Delete Confirmation Dialog */}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={(e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          if (deleteTeacherMutation.isLoading) return;
          setDeleteConfirmOpen(false);
          setTeacherToDelete(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Teacher</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to delete{" "}
            <strong>{teacherToDelete?.name || "this teacher"}</strong>? This
            action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteConfirmOpen(false);
              setTeacherToDelete(null);
            }}
            disabled={deleteTeacherMutation.isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmDeleteTeacher}
            disabled={deleteTeacherMutation.isLoading}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Teachers;
